import React, { useEffect, useState } from "react";
import "./PollComponent.scss";
import { useTranslation } from "react-i18next";
import { useDispatch, useSelector } from "react-redux";
import { userDetails } from "../../../../../state/user/selectors";
import { voteNotificationPoll } from "../../../../../state/notifications/actions";
import { isPollVoteInProgress } from "../../../../../state/notifications/selectors";
import OnlyMember from "../../../../common/only-member/OnlyMember";
import Radio from "../../../../common/form/radio/Radio";
import classNames from "classnames";
import HasReadPrivileges from "../../../../common/with-privileges/HasReadPrivileges";
import { ChangeOfficalPollModal } from "./ChangeOfficalPollModal";
import ModalWrapper from "../../../../common/modal-wrapper/ModalWrapper";
import { PollVoterList } from "./PollVoterList";
import HasWritePrivileges from "../../../../common/with-privileges/HasWritePrivileges";

export const PollComponent = ({ poll }) => {
  const {
    options,
    votes,
    isMultiVote,
    deadline: orgDeadline,
    enableDeadline,
  } = poll;

  const { t } = useTranslation("common");
  const dispatch = useDispatch();
  const userId = useSelector(userDetails)?.id;
  const isVoting = useSelector(isPollVoteInProgress);
  const isDeadlinePassed = new Date(orgDeadline) < new Date();
  const [selectedOptions, setSelectedOptions] = useState([]);
  const [pendingOptionId, setPendingOptionId] = useState(null);

  const [showPollVoterList, setShowPollVoterList] = useState(false);
  const [selectedOptionId, setSelectedOptionId] = useState(null);
  const [showChangeOfficialPollModal, setShowChangeOfficialPollModal] =
    useState(false);

  useEffect(() => {
    if (votes) {
      const userVotes = votes.filter((vote) => vote.userId === userId);
      setSelectedOptions(
        userVotes.map((vote) => ({
          pollOptionId: vote.pollOptionId,
          pollVoteId: vote.id,
        })),
      );
    }
  }, [votes, userId]);

  const calculateBar = (optionId) => {
    const optionVotes = votes.filter(
      (vote) => vote.pollOptionId === optionId,
    ).length;
    const totalVotes = votes.length || 1;
    return optionVotes / totalVotes;
  };

  // ponytail: one vote at a time, whole poll. Serialization is what keeps the
  // single-refetch guarantee - letting multi-vote options run concurrently
  // brings back a refetch per request racing to land last. Cost is that a click
  // on a second option during the round trip is dropped rather than queued.
  // Upgrade path if that bites: queue pending option ids and drain them in one
  // saga run, still refetching once at the end.
  const handleOptionChange = (optionId, selectedOption) => {
    // A vote is already on the wire: ignore. Guards the window between the
    // click and React re-rendering the inputs as disabled.
    if (isVoting) return;
    if (enableDeadline && isDeadlinePassed) return;

    // Unchecking clears it; in a single-choice poll checking another option
    // replaces the existing vote. Both go out as one action so the removal and
    // the new vote are sequenced server-side instead of racing.
    const removedVote = selectedOption
      ? selectedOption
      : !isMultiVote && selectedOptions.length > 0
        ? selectedOptions[0]
        : null;

    setPendingOptionId(optionId);
    dispatch(
      voteNotificationPoll({
        pollOptionId: selectedOption ? null : optionId,
        pollVoteId: removedVote ? removedVote.pollVoteId : null,
      }),
    );
  };

  const showPollVoterListHandler = (optionId) => {
    setSelectedOptionId(optionId);
    setShowPollVoterList(true);
  };

  const renderOptions = () => {
    return options.map((option) => {
      const selectedOption = selectedOptions.find(
        (selectedOption) => selectedOption.pollOptionId === option.id,
      );

      const percentage = calculateBar(option.id);

      return (
        <div key={option.id} className="voting-bar__wrapper">
          {(!enableDeadline || (enableDeadline && !isDeadlinePassed)) && (
            <OnlyMember>
              {/* Same control the deadline voter uses, so the two look alike
                  and the spinner REPLACES the box rather than sitting beside
                  it - rendering both is what broke the row. */}
              <Radio
                inputType="checkbox"
                checked={!!selectedOption}
                disabled={isVoting}
                isAwaiting={isVoting && pendingOptionId === option.id}
                onChange={() => handleOptionChange(option.id, selectedOption)}
              />
            </OnlyMember>
          )}
          <div className="voting-bar">
            <span>{option.value}</span>
            <div
              className="voting-bar__votes yes"
              style={{ transform: `scaleX(${percentage})` }}
            ></div>
            <div className="voting-bar__vote-count">
              <p className="poll-percentage">
                <span>{`${(percentage * 100).toFixed(2)}%`}</span>
                <button
                  className={classNames(
                    "poll-count",
                    "button",
                    "button--link",
                    "button--no-border",
                  )}
                  disabled={option.count === 0}
                  onClick={() =>
                    option.count > 0
                      ? showPollVoterListHandler(option.id)
                      : null
                  }
                >{`${option.count} vote${option.count > 1 ? "s" : ""}`}</button>
              </p>
            </div>
          </div>
        </div>
      );
    });
  };

  const renderFooter = () => {
    return (
      <div className="official-vote">
        <div className="official-vote__bar">
          <span className="official-vote__label">
            {t("notifications.poll.mainLabel", {
              vote: poll.result,
            })}
          </span>
          <HasWritePrivileges>
            <button
              className="official-vote-button official-vote__link button button--link button--no-border"
              onClick={() => setShowChangeOfficialPollModal(true)}
            >
              {t("notifications.poll.button.changeResult")}
            </button>
          </HasWritePrivileges>
        </div>
        <HasWritePrivileges>
          <span className="official-vote__note">
            {t("deadlines.details.officialVote.note")}
          </span>
        </HasWritePrivileges>
      </div>
    );
  };

  return (
    <>
      <div className={classNames("group", "group--underlined")}>
        <div className="voter">
          <h3>{poll.title}</h3>
          {renderOptions()}
        </div>
        {renderFooter()}
      </div>
      {showPollVoterList && (
        <ModalWrapper
          isOpen={showPollVoterList}
          requestCloseHandler={() => setShowPollVoterList(false)}
          disableSlide
        >
          <PollVoterList
            closeHandler={() => setShowPollVoterList(false)}
            selectedOptionId={selectedOptionId}
          />
        </ModalWrapper>
      )}
      {showChangeOfficialPollModal && (
        <ModalWrapper
          isOpen={showChangeOfficialPollModal}
          requestCloseHandler={() => setShowChangeOfficialPollModal(false)}
          disableSlide
        >
          <ChangeOfficalPollModal
            onClose={() => setShowChangeOfficialPollModal(false)}
          />
        </ModalWrapper>
      )}
    </>
  );
};
